import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToggleLeft, ToggleRight, Info } from 'lucide-react';

/**
 * Feature flag registry. Add a flag here with a label + description and it
 * automatically appears in the dev banner's Feature Flags panel. Flags persist
 * to localStorage (per-browser) so they survive refreshes — handy for demoing
 * or QA-ing an in-progress feature without a backend deploy.
 *
 * Read a flag in any component with: const on = useFeatureFlag('flagKey');
 */
export const FEATURE_FLAG_REGISTRY = [
  {
    key: 'newUI',
    label: 'New UI shell',
    description: 'Opt into redesigned navigation / layout while it is in progress.',
    default: false,
  },
  {
    key: 'betaFeature',
    label: 'Beta features',
    description: 'Generic gate for features that are dev-complete but not yet released.',
    default: false,
  },
  {
    key: 'experimentalFeature',
    label: 'Experimental',
    description: 'Unstable / spike work. May break — for internal evaluation only.',
    default: false,
  },
  {
    key: 'anotherFeature',
    label: 'Misc toggle',
    description: 'Placeholder slot for ad-hoc toggling during development.',
    default: false,
  },
];

const FLAGS_STORAGE_KEY = 'feature_flags';

const buildInitialFlags = () => {
  const defaults = Object.fromEntries(FEATURE_FLAG_REGISTRY.map((f) => [f.key, f.default]));
  try {
    const stored = JSON.parse(localStorage.getItem(FLAGS_STORAGE_KEY)) || {};
    // Registry defines the shape; stored values only override known keys.
    return { ...defaults, ...Object.fromEntries(Object.entries(stored).filter(([k]) => k in defaults)) };
  } catch {
    return defaults;
  }
};

const FeatureFlagsContext = React.createContext();

const FeatureFlags = ({ children }) => {
  const [featureFlags, setFeatureFlags] = useState(buildInitialFlags);
  const location = useLocation();

  // Persist on every change so flags survive a refresh.
  useEffect(() => {
    try {
      localStorage.setItem(FLAGS_STORAGE_KEY, JSON.stringify(featureFlags));
    } catch {
      /* ignore storage failures */
    }
  }, [featureFlags]);

  const toggleFeature = (feature) => {
    setFeatureFlags(prevFlags => ({
      ...prevFlags,
      [feature]: !prevFlags[feature],
    }));
  };

  const resetFeatureFlags = () => {
    setFeatureFlags(Object.fromEntries(FEATURE_FLAG_REGISTRY.map((f) => [f.key, f.default])));
  };

  return (
    <FeatureFlagsContext.Provider value={{ featureFlags, toggleFeature, resetFeatureFlags, registry: FEATURE_FLAG_REGISTRY }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

const FeatureFlagToggle = ({ feature, isEnabled, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 hover:shadow-lg dark:hover:shadow-gray-900/20 transition-shadow duration-300"
    >
      <div className="flex items-center space-x-3">
        <Info className="w-5 h-5 text-primary-600" />
        <span className="text-lg font-medium text-gray-700">{feature}</span>
      </div>
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => onToggle(feature)}
        className={`p-2 rounded-full transition-colors duration-300 ${
          isEnabled ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
        }`}
      >
        {isEnabled ? (
          <ToggleRight className="w-8 h-8" />
        ) : (
          <ToggleLeft className="w-8 h-8" />
        )}
      </motion.button>
    </motion.div>
  );
};

const FeatureFlagsManager = () => {
  const { featureFlags, toggleFeature } = React.useContext(FeatureFlagsContext);

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Feature Flags</h2>
      <AnimatePresence>
        {Object.entries(featureFlags).map(([feature, isEnabled]) => (
          <FeatureFlagToggle
            key={feature}
            feature={feature}
            isEnabled={isEnabled}
            onToggle={toggleFeature}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

export { FeatureFlags, FeatureFlagsContext, FeatureFlagsManager };